import React, { useState, useMemo } from 'react';
import { useInventoryPlanning } from '../../contexts/InventoryPlanningContext';
import { useDatabaseContext } from '../../contexts/DatabaseContext';
import MonthlyPivotTable from '../MonthlyPivotTable';
import SelectSupplierLocationModal from '../SelectSupplierLocationModal';
import ArticleIdCell from '../ArticleIdCell';
import { Download } from 'lucide-react';
import { pivotSalesToCSV } from '../../utils/csvExport';
import api from '../../services/api';
import { API_ROUTES } from '../../config/api';
import { formatIntegerWithColor } from '../../utils/numberFormatting';

const ForecastTab: React.FC = () => {
  const { data, selectedArticleIds, ensureSections, sectionStates } = useInventoryPlanning();
  const { activeDatabase } = useDatabaseContext();
  React.useEffect(() => {
    ensureSections(['forecast']);
  }, [ensureSections]);
  const sectionState = sectionStates.forecast;
  const allRows = useMemo(() => data?.forecast_series || [], [data?.forecast_series]);
  const [modalArticle, setModalArticle] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  // Filter forecast series by selected articles
  const rows = useMemo(() => {
    if (!selectedArticleIds || selectedArticleIds.length === 0) {
      return allRows; // Export all if no selection
    }
    return allRows.filter((row: any) => 
      selectedArticleIds.includes(String(row.article || row.ref))
    );
  }, [allRows, selectedArticleIds]);

  const modelsToTry = ['autogluon', 'HYBRID', 'TCN', 'NBEATS', 'SARIMA', 'LSTM'];

  const handleExportForecasts = async () => {
    if (!activeDatabase?.id) {
      setExportError('Aucune base de données sélectionnée');
      return;
    }

    setIsExporting(true);
    setExportError(null);

    try {
      // Use existing forecast_series from context - it already has all individual predictions
      // The backend fecth_sales_predicted already tries models in priority order
      if (rows && rows.length > 0) {
        const predictions = rows.map((row: any) => ({
          article: String(row.article || row.ref),
          date: row.date,
          sales: Number(row.sales || 0),
        }));

        const csvContent = pivotSalesToCSV(predictions, 'article', 'date', 'sales');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `previsions_${new Date().toISOString().slice(0, 10)}.csv`;
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        setIsExporting(false);
        return;
      }

      // Fallback: If forecast_series is empty, try fetching from API with model priority
      const filters: any = {};
      if (selectedArticleIds && selectedArticleIds.length > 0) {
        filters.ref = selectedArticleIds;
      }

      let predictions: any[] = [];
      let modelUsed: string | null = null;

      // Try models in priority order
      for (const modelName of modelsToTry) {
        try {
          const response = await api.post(
            `${API_ROUTES.PREDICTION.BASE}${API_ROUTES.PREDICTION.PREDICTION_DATA(String(activeDatabase.id), modelName)}`,
            {
              model_name: modelName,
              filters,
            }
          );

          if (response.data?.predictions && Array.isArray(response.data.predictions) && response.data.predictions.length > 0) {
            // API returns aggregated by month, but we need per-article
            // Since we can't get individual predictions from this endpoint,
            // we'll create entries for each selected article with monthly aggregates
            const articles = selectedArticleIds && selectedArticleIds.length > 0 
              ? selectedArticleIds 
              : ['all_articles'];
            
            predictions = response.data.predictions.flatMap((p: any) =>
              articles.map((article: string) => ({
                article,
                date: p.date,
                sales: p.sales || 0,
              }))
            );
            modelUsed = modelName;
            break;
          }
        } catch (error: any) {
          // Continue to next model
          continue;
        }
      }

      if (predictions.length === 0) {
        setExportError('Aucune prédiction disponible pour les articles sélectionnés');
        setIsExporting(false);
        return;
      }

      // Export to CSV
      const csvContent = pivotSalesToCSV(predictions, 'article', 'date', 'sales');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `previsions_${modelUsed || 'unknown'}_${new Date().toISOString().slice(0, 10)}.csv`;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setIsExporting(false);
    } catch (error: any) {
      setExportError('Erreur lors de l\'export des prévisions');
      setIsExporting(false);
    }
  };

  return (
    <div className="bg-white shadow rounded p-4" style={{ minWidth: '600px' }}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-medium">Prévisions</div>
        <button
          onClick={handleExportForecasts}
          disabled={isExporting}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors border border-blue-200 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Exporter toutes les prévisions"
        >
          <Download size={14} />
          {isExporting ? 'Export...' : 'Exporter Prévisions'}
        </button>
      </div>
      {exportError && (
        <div className="mb-2 p-2 bg-red-50 border border-red-200 text-red-700 rounded text-xs">
          {exportError}
        </div>
      )}
      {sectionState?.loading && <div className="text-xs text-gray-500 mb-2">Chargement des prévisions…</div>}
      {sectionState?.error && <div className="text-xs text-red-600 mb-2">{sectionState.error}</div>}
      <div className="text-xs text-gray-500 mb-2">{rows.length} lignes</div>
      <MonthlyPivotTable
        rows={rows}
        idField="article"
        dateField="date"
        valueField="sales"
        idHeader="Article"
        actionHeader=""
        onRowClick={(id) => setModalArticle(String(id))}
        renderIdCell={(id) => <ArticleIdCell id={id} onClick={() => setModalArticle(id)} />}
        valueFormatter={(v) => formatIntegerWithColor(v)}
      />
      <SelectSupplierLocationModal open={!!modalArticle} articleId={modalArticle} onClose={() => setModalArticle(null)} />
    </div>
  );
};

export default ForecastTab;


